import isDrawableImage from './is-drawable-image';

/**
 * Calls `onFailure` when an image cannot be displayed.
 *
 * Covers both cases:
 *  - the image is still loading, so the `error` event is enough
 *  - the image is already broken, typically served from cache, so the error event has
 *    been and gone before we could attach a listener
 *
 * The already-broken notification is deferred to a microtask because callers invoke this
 * from React lifecycle methods, and setting state on a parent synchronously from there
 * lands inside React's render phase.
 *
 * Returns a teardown. Call it on unmount: LocalThumbCache keeps its Image objects in a
 * module-level map that is never evicted, so a listener left attached outlives the
 * component that added it and keeps it alive. Always safe to call.
 *
 * @param {HTMLImageElement|null} image
 * @param {Function} onFailure
 * @returns {Function} teardown
 */
const noop = () => {};

const observeImageFailure = (image, onFailure) => {
  if (!image || typeof onFailure !== 'function') return noop;

  // No src yet means nothing has been attempted, so this is not a failure.
  if (image.src && image.complete && !isDrawableImage(image)) {
    // Cancellable: the teardown runs before a re-observe and on unmount, and the photo
    // may have picked up a working thumb_url in the same task. Firing anyway would pin
    // the terminal state over a healthy thumbnail for the rest of the session.
    let cancelled = false;
    Promise.resolve().then(() => {
      if (!cancelled) onFailure();
    });
    return () => {
      cancelled = true;
    };
  }

  if (typeof image.addEventListener !== 'function') return noop;

  image.addEventListener('error', onFailure);

  return () => {
    if (typeof image.removeEventListener === 'function') {
      image.removeEventListener('error', onFailure);
    }
  };
};

export default observeImageFailure;
